import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useNavigate } from "react-router-dom";

import { Container } from "@magnetic/container";
import { Divider } from "@magnetic/divider";
import { Flex } from "@magnetic/flex";
import { Header } from "@magnetic/header";
import { Heading } from "@magnetic/heading";
import { Code, PaintBrush } from "@magnetic/icons";
import { Skeleton } from "@magnetic/skeleton";

import { CiscoLogo } from "src/common";
import { Button } from "src/components/buttons";
import { HFlex } from "src/components/hFlex";
import { Link } from "src/components/link";
import { ThemeToggle } from "src/components/themeToggle";
import { UserRole } from "src/gen/schema/models/auth";
import { useUrlState } from "src/hooks/useUrlState";
import { MockShellGlobalHeader } from "src/pages/mock-shell/mockShellGlobalHeader";
import { TimeZoneSection } from "src/pages/timezone/TimeZoneSection";
import { Constants } from "src/utils/constants";

import { DataProviderContext } from "./provider/dataProvider";
import { LoginContext } from "./loginContext";
import { RestHistoryMonitor } from "./restHistoryMonitor";
import { StreamingCacheMonitor } from "./streamingCacheMonitor";

import "./globalHeader.scss";

const PendingFabricChanges = () => {
  const { events, fabric } = useContext(DataProviderContext);
  if (fabric?.id) {
    return (
      <Header.Dropdown
        className="pending-changes"
        icon="deploy"
        label="Pending changes"
        panel={false}
        alert={events?.length || 0}
      >
        <></>
      </Header.Dropdown>
    );
  }
  return null;
};

interface HelpContentMetadata {
  label: string;
  url: string;
}
// Grouping of help menu items and respective target web page links
const helpContentMetadataList: Array<Array<HelpContentMetadata>> = [
  [
    {
      label: "Release notes",
      url: "//www.cisco.com/c/en/us/docs/technical-documentation.html?product=N4IgTgpgzgrgNgFxALlABzAewCYwMYIByAhgLYQogDCAllHpgASEQAeMUjAEgJ5oRgAZsQBGYGnhAAaEAhoI4FZCBbtOvfkNHjJMgHY4IASWyUATAA4AbAGYbAdgAMVq9JBpikPUUMnz1m0cLexsATjdSbEEqYgQIAHNMMB5KAAUsXAIoNzgaQQgqHjxFSmICGgA3Cn1DABU%2BJRAoARpoNwNsCABZKK6IBGIqOGIobOUAZUxBBAB3TwhGADEyGjgUmWw6NGGeABk8gqKS5TcITAQAEUw8MccAXzugA&selectedFilters=N4IgZglgNgLgpgJwM4gFygCYHsDGBhAQ3gHMsEI4VUBtEAJTijgKTgAIBJAOzDIFsiELFxABdADQgEjZqyq0AglChiAvpKRkYAIQCeaEFzgB3SjBCqgA"
    },
    {
      label: "Get started",
      url: "//www.cisco.com/c/en/us/td/docs/dcn/hyperfabric/collections/hyperfabric-user-content.html#expanded-tabs-d3d0761755-item-8d8162e45a-tabpanel"
    },
    {
      label: "Subscriptions",
      url: "//www.cisco.com/c/en/us/td/docs/dcn/hyperfabric/software/cisco-nexus-hyperfabric-subscription.html"
    },
    {
      label: "Deploy your fabric on site",
      url: "//www.cisco.com/c/en/us/td/docs/dcn/hyperfabric/software/cisco-nexus-hyperfabric-on-site.html"
    }
  ],
  [
    {
      label: "AI assistant for technical documentation",
      url: "//www.cisco.com/c/en/us/docs/technical-documentation.html?product=N4IgTgpgzgrgNgFxALlABzAewCYwMYIByAhgLYQogDCAllHpgASEQAeMUjAEgJ5oRgAZsQBGYGnhAAaEAhoI4FZCBbtOvfkNHjJMgHY4IASWyUATAA4AbAGYbAdgAMVq9JBpikPUUMnz1m0cLexsATjdSbEEqYgQIAHNMMB5KAAUsXAIoNzgaQQgqHjxFSmICGgA3Cn1DABU%2BJRAoARpoNwNsCABZKK6IBGIqOGIobOUAZUxBBAB3TwhGADEyGjgUmWw6NGGeABk8gqKS5TcITAQAEUw8MccAXzugA"
    }
  ]
] as const;

export default function GlobalHeader() {
  const [searchText, setSearchText] = useUrlState("g");
  const { session, logout } = useContext(LoginContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const openHelpContentUrl = useCallback((item: HelpContentMetadata) => {
    window.open(item.url, "_blank");
  }, []);

  const handleSearchChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setSearchText(e.currentTarget.value);
    },
    [setSearchText]
  );

  const isDevMode = window.cnc.settings.get("dev");
  const showRestHistory = window.cnc.settings.get("restHistory");
  const [openMonitor, setOpenMonitor] = useState<
    "rest" | "streaming" | undefined
  >();

  useEffect(() => {
    if (openMonitor === undefined) {
      return undefined;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenMonitor(undefined);
        // Magnetic's Modal restores focus to the element that was focused
        // before the modal opened (i.e. the monitor trigger button) when it
        // unmounts. That restoration runs synchronously after React commits
        // the state change above, so blurring here (or inline) would fire
        // BEFORE the restoration and be immediately overwritten. Defer the
        // blur to the next task so it runs after Magnetic has re-focused
        // the trigger, leaving nothing focused.
        setTimeout(() => {
          (document.activeElement as HTMLElement)?.blur();
        }, 0);
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [openMonitor]);
  const menuItemSize = 24;

  const dropdownItems: {
    id: string;
    content: JSX.Element;
    icon?: JSX.Element;
    hide?: boolean;
  }[] = [
    {
      id: "themeSwitch",
      icon: <PaintBrush weight="bold" size={menuItemSize} className="icon" />,
      content: <ThemeToggle />
    },
    {
      id: "timeZoneSelector",
      content: <TimeZoneSection />
    },
    {
      id: "tokensLink",
      icon: <Code weight="bold" size={menuItemSize} className="icon" />,
      hide:
        session?.role !== UserRole.ADMIN &&
        session?.role !== UserRole.READ_WRITE,
      content: (
        <Button
          name="apiBearerTokens"
          className="api-bearer-tokens"
          kind="tertiary"
          onClick={() => {
            setIsDropdownOpen(false);
            navigate("/api-bearer-tokens");
          }}
        >
          API bearer tokens
        </Button>
      )
    },
    {
      id: "logoutButton",
      content: (
        <Flex justify="right">
          <Button
            name="logout"
            className="logout"
            kind="secondary"
            onClick={logout}
          >
            Log out
          </Button>
        </Flex>
      )
    }
    /* <Button
      name="changePassword"
      className="change-password"
      kind="tertiary"
      onClick={() => changePassword(() => {})}
    >
      Change Password
    </Button> */
  ];

  return (
    <Header
      href="/"
      className="global-header"
      title={session?.tenantName}
      logo={<CiscoLogo />}
      productName={`${Constants.APP_NAME}${
        isDevMode ? ` - Developer Mode - ${__APP_VERSION__}` : ""
      }`}
      profileAndTenant={{
        icon: "user",
        profile: {
          heading: session?.email || Constants.EMPTY_VALUE,
          subHeading: session?.tenantName || Constants.EMPTY_VALUE
        },
        isOpen: isDropdownOpen,
        setIsOpen: setIsDropdownOpen,
        content: (
          <Container style={{ width: 362 }} className="user-profile">
            <Flex
              className="user-profile-actions"
              direction="vertical"
              gap="md"
            >
              {dropdownItems
                .filter(({ hide }) => !hide)
                .map(({ id, content, icon }, index, filteredItems) => (
                  <div key={id}>
                    <Flex className="dropdown-item" align="center">
                      <div className="icon-container">{icon}</div>
                      <div className="content-container">{content}</div>
                    </Flex>
                    {index !== filteredItems.length - 1 && <Divider />}
                  </div>
                ))}
            </Flex>
          </Container>
        )
      }}
      showXPN={false}
    >
      <Header.Menu icon="question" label="Help">
        {helpContentMetadataList.map(
          (helpContentGroup, immutableGroupIndex) => (
            <Fragment key={+immutableGroupIndex}>
              {helpContentGroup.map((helpContentMetadata) => (
                <Header.MenuItem
                  key={helpContentMetadata.label}
                  onClick={() => openHelpContentUrl(helpContentMetadata)}
                  label={helpContentMetadata.label}
                  href="#"
                />
              ))}
              {immutableGroupIndex < helpContentMetadataList.length - 1 && (
                <Header.MenuDivider />
              )}
            </Fragment>
          )
        )}
      </Header.Menu>

      {isDevMode && false /* Disable until we support this */ && (
        <Header.Search
          label="example search"
          panel={true}
          open={!!searchText}
          onChange={handleSearchChange}
        >
          <Container style={{ width: 500 }}>
            <Heading size="h1">Results for {`"${searchText}"`}</Heading>
            <HFlex>
              <Heading>Switches Top 5</Heading>
              <Link
                name="allSwitches"
                targetRoute={`/globalSearch/switches/${searchText}`}
              >
                All Switches
              </Link>
            </HFlex>

            <Flex direction="vertical" gap="md">
              <Skeleton height="lg" />
              <Skeleton height="md" />
              <Skeleton height="sm" />
              <Skeleton height={72} />
            </Flex>
            <HFlex>
              <Heading>Ports Top 5</Heading>
              <Link
                name="allPorts"
                targetRoute={`/globalSearch/ports/${searchText}`}
              >
                All Ports
              </Link>
            </HFlex>
            <Flex direction="vertical" gap="md">
              <Skeleton height="lg" />
              <Skeleton height="md" />
              <Skeleton height="sm" />
              <Skeleton height={72} />
            </Flex>
          </Container>
        </Header.Search>
      )}
      <MockShellGlobalHeader />
      <div className="cnc-monitor-buttons">
        {isDevMode && showRestHistory && (
          <RestHistoryMonitor
            isOpen={openMonitor === "rest"}
            onToggle={() =>
              setOpenMonitor((prev) => (prev === "rest" ? undefined : "rest"))
            }
          />
        )}
        {isDevMode && (
          <StreamingCacheMonitor
            isOpen={openMonitor === "streaming"}
            onToggle={() =>
              setOpenMonitor((prev) =>
                prev === "streaming" ? undefined : "streaming"
              )
            }
          />
        )}
      </div>

      {/* <Header.Button icon="info" label="info" onClick={handleClick} /> */}
      {/*
          <Header.Dropdown icon="question" label="question" panel={true}>
            <Container style={{ width: 500 }}>
              <Flex direction="vertical" gap="md">
                <Skeleton height="lg" />
                <Skeleton height="md" />
                <Skeleton height="sm" />
                <Skeleton height={72} />
              </Flex>
            </Container>
          </Header.Dropdown>
          <Header.Dropdown icon="bell" label="question" alert={81} panel={true}>
            <Container style={{ width: 500 }}>
              <Flex direction="vertical" gap="md">
                <Skeleton height="lg" />
                <Skeleton height="md" />
                <Skeleton height="sm" />
                <Skeleton height={72} />
              </Flex>
            </Container>
          </Header.Dropdown> */}
      {/* <Header.Button
        alert={100}
        icon="bell"
        label="example dropdown"
        onClick={console.log}
      /> */}
      <PendingFabricChanges />
    </Header>
  );
}
